//type AConstructorTypeOf<T> = new (...args: any[]) => T;
import {IMyopComponent} from "./components/IMyopComponent";
import {CreateRefComponentMessage, CustomRefMessage, Ref} from "../messages";
import * as messages from "../messages";
import {
    CleanupHandler, IComponentConfig,
    type IComponentDefinitionConfig,
    IComponentRefBehavior, IHTMLLoaderConfig, IPropCodeBehavior,
    IPropConfig, ISkinConfig, PropConfigBehaviorTypes,
    RefBehaviorType, ShadowRootModeType, SkinLoaders, SkinLoaderType
} from "../common";
import {containsPrimitivesOnly} from "../common/utils/serialization";
import {Errors} from "./logMessages";
import {MyopRefComponent} from "./components/myopRefComponent";
import {MyopExtractedWebComponent} from "./components/myopExtractedWebComponent";
import {loaderOptions} from "./hostSDK";

type componentDefinitionProps = IComponentDefinitionConfig['props']

const createDebugTitle = (text: string, top: number, componentDefinitionConfig: IComponentDefinitionConfig) => {
    const titleEl = document.createElement('a');
    titleEl.textContent = text;
    titleEl.style.position = 'relative';
    titleEl.style.padding = '0 5px';
    titleEl.style.fontSize = '14px';
    titleEl.style['top'] = '0';
    titleEl.style.top = `${top}px`;
    //titleEl.style.left = '50%';
    titleEl.style.transform = 'translateX(-50%)';
    titleEl.target = '_blank';
    titleEl.href = `https://dashboard.myop.dev/dashboard/component/${componentDefinitionConfig.id}`

    return titleEl;
};

export const setDebugStyle = (id: string,
                              type: string,
                              top: number,
                              container: HTMLElement,
                              componentElement: HTMLElement,
                              componentDefinitionConfig: IComponentDefinitionConfig
): CleanupHandler => {
    //#007BFF (blue) or #FF5722 (orange)
    //#4CAF50 (green) or #FFC107 (amber)
    // const topText = createDebugTitle(`${id} : ${type}`, top, componentDefinitionConfig);
    const topText = createDebugTitle(`${componentDefinitionConfig.name} : ${type}`, top, componentDefinitionConfig);
    container.insertBefore(topText, componentElement);
    componentElement.style.border = '1px solid #007BFF';
    componentElement.style.display = 'block';

    return () => {
        componentElement.style.border = 'unset';
        container.removeChild(topText);
    }
}

const reportError = (propConfig: IPropConfig, propName: string, component: IMyopComponent<any, any>) => {
    if (!propConfig) {

        console.error(
            // throw new Error(
            `Error: Undefined Prop\n\n` +
            `It looks like you've tried to use a prop that hasn't been defined.\n` +
            `Please check the prop name for any typos or ensure that it is properly defined in the component's prop list.\n\n` +
            `Prop Name: ${propName}\n` +
            `Component: ${component.componentDefinition.name}, ID: ${component.componentDefinition.id}\n\n` +
            `For more information, refer to the component page https://dashboard.myop.dev/dashboard/component/${component.componentDefinition.id} or consult the developer guide.`
        );
        return true
    }
    return false
}

const reportUnsupported = (propConfig: IPropConfig, propName: string, component: IMyopComponent<any, any>) => {
    console.error(
        `Error: Code Prop Not Supported\n\n` +
        `Currently, code props are only supported in local frame components.\n` +
        `⚠️ This is a work in progress feature — code props will be supported in upcoming versions.\n\n` +
        `In the meantime, you can use message props.\n\n` +
        `Prop Name: ${propName}\n` +
        `Component: ${component.componentDefinition.name}, ID: ${component.componentDefinition.id}\n\n` +
        `For more information, refer to the component page https://dashboard.myop.dev/dashboard/component/${component.componentDefinition.id} or consult the developer guide.`
    );
}

export const connectProps = <T extends componentDefinitionProps>(component: IMyopComponent, propsConfig: IPropConfig[], selectedSkin: ISkinConfig) => {

    const propsMap = propsConfig.reduce((previousValue, currentValue) => {
        return {
            ...previousValue,
            [currentValue.name]: {...currentValue}
        }
    }, {});

    const propsHandler = {
        get: (_: IMyopComponent, prop: string) => {
            // @ts-ignore
            const propConfig = propsMap[prop];

            if (reportError(propConfig, prop, component)) {
                return false;
            }

            if (propConfig.behavior.type === PropConfigBehaviorTypes.code) {
                // Check for extracted web components FIRST
                if (component instanceof MyopExtractedWebComponent) {
                    const typedPropBehavior = propConfig.behavior as IPropCodeBehavior;
                    const propName = typedPropBehavior.remap || prop;

                    if (propName === 'myop_init_interface') {
                        // Return a function that gets/sets data (matches iframe behavior)
                        return (data?: unknown) => {
                            if (data === undefined) {
                                return component.getData();
                            }
                            component.initWithData(data);
                        };
                    }

                    // For myop_cta_handler, return undefined (it's write-only)
                    return undefined;
                }

                // For iframe (localFrame) components
                if (selectedSkin.loader.type === SkinLoaders.HTMLLoader) {
                    const typedLoader = selectedSkin.loader as IHTMLLoaderConfig;
                    if (typedLoader.shadowRootMode === ShadowRootModeType.localFrame) {
                        const typedPropBehavior = propConfig.behavior as IPropCodeBehavior;
                        // @ts-ignore
                        return component.element.contentWindow[typedPropBehavior.remap || prop]
                    }
                }

                reportUnsupported(propConfig, prop, component);
                return false;
            }

            if (propConfig.mode === 'output') {
                return new Promise((resolve) => {
                    const messageParams: any[] = [];
                    //TODO : turn it into config
                    if (propConfig.behavior.ref) {
                        messageParams.push(Ref.create(propConfig.behavior.ref));
                    }

                    messageParams.push((data: any) => {
                        resolve(data);
                    });

                    if (propConfig.behavior.params) {
                        messageParams.push(...propConfig.behavior.params);
                    }

                    // @ts-ignore
                    const messageCtor = messages[propConfig.behavior.message];

                    component.send(new messageCtor(...messageParams));
                })
            }
            return null
        },
        set: (_: IMyopComponent, prop: string, value: any) => {
            // @ts-ignore
            const propConfig = propsMap[prop];

            if (reportError(propConfig, prop, component)) {
                return false;
            }

            if (propConfig.behavior.type === PropConfigBehaviorTypes.code) {
                // Check for extracted web components FIRST (before localFrame check)
                // because extracted web components may have shadowRootMode: localFrame in their config
                // but they run directly on the host document, not in an iframe
                if (component instanceof MyopExtractedWebComponent) {
                    const typedPropBehavior = propConfig.behavior as IPropCodeBehavior;
                    const propName = typedPropBehavior.remap || prop;

                    if (propName === 'myop_cta_handler') {
                        component.setCtaHandler(value);
                        return true;
                    }

                    if (propName === 'myop_init_interface') {
                        component.initWithData(value);
                        return true;
                    }
                }

                // For iframe (localFrame) components
                if (selectedSkin.loader.type === SkinLoaders.HTMLLoader) {
                    const typedLoader = selectedSkin.loader as IHTMLLoaderConfig;
                    if (typedLoader.shadowRootMode === ShadowRootModeType.localFrame) {
                        const typedPropBehavior = propConfig.behavior as IPropCodeBehavior;
                        const contentWindow = (component.element as HTMLIFrameElement).contentWindow as any;
                        const propName = typedPropBehavior.remap || prop;

                        // Set the real handler
                        contentWindow[propName] = value;

                        // Flush CTA queue if this is myop_cta_handler and queue exists
                        if (propName === 'myop_cta_handler' && contentWindow.__myop_cta_queue) {
                            const queue = contentWindow.__myop_cta_queue;
                            contentWindow.__myop_cta_queue = null; // Clear to prevent double-flush
                            queue.forEach((item: {action: string, payload: any}) => {
                                // Skip if component was disposed during flush
                                if (component.markedForDisposed) return;
                                value(item.action, item.payload);
                            });
                        }

                        return true
                    }
                }

                reportUnsupported(propConfig, prop, component);
                return false;
            } else {
                //if(propConfig.behavior === PropConfigBehaviorTypes.message)
                if (propConfig.mode === 'input') {
                    //TODO : support multi behaviors

                    if (propConfig.behavior.type === 'message') {
                        const messageParams: any[] = [];
                        //TODO : turn it into config
                        if (propConfig.behavior.ref) {
                            messageParams.push(Ref.create(propConfig.behavior.ref));
                        }
                        messageParams.push(value);
                        if (propConfig.behavior.params) {
                            messageParams.push(...propConfig.behavior.params);
                        }

                        // @ts-ignore
                        const messageCtor = messages[propConfig.behavior.message];
                        component.send(new messageCtor(...messageParams));
                        return true
                    } else {
                        throw new Error(
                            `Error: Unsupported Behavior\n\n` +
                            `The 'behavior' field provided is not supported.\n` +
                            `Component: ${component.componentDefinition.name}, ID: ${component.componentDefinition.id}\n\n` +
                            `Prop Name: ${prop}\n` +
                            `Behavior Field: ${propConfig.behavior.type}\n\n` +
                            `Check the documentation for valid behavior options.`
                        );

                        return false
                    }
                }
            }
            return false
        }
    }

    component.props = {};
    component.props = new Proxy(component.props, propsHandler);

    return component as (Omit<IMyopComponent, 'props'> & {
        props: propTypes<T>
    })
}

export const createRefProxy = (target: Ref, component: IMyopComponent) => {
    return new Proxy(target, {
        get(target, prop) {

            // if (prop === '__nonProxied') {
            //     return target;
            // }

            // @ts-ignore
            if (target[prop]) {
                // @ts-ignore
                return target[prop];
            }

            // @ts-ignore
            const getterFunction = (...args) => {
                return new Promise((resolve) => {
                    args.forEach(arg => {
                        if (!containsPrimitivesOnly(arg)) {
                            Errors.notSerializableRefCall(prop, arg);
                        }
                    });
                    component.send(new CustomRefMessage(({ref, propName, functionArgs, makeSerializable}) => {
                            if (ref) {
                                // @ts-ignore
                                const value = ref[propName];
                                if (typeof value === 'function') {
                                    // @ts-ignore
                                    return makeSerializable(ref[propName](...functionArgs), true) // Invoke the function and return its result
                                } else {
                                    // @ts-ignore
                                    return makeSerializable(value, true); // Return the property value
                                }
                            }
                            return null;
                        },
                        {
                            //TODO : turn it to config
                            elementId: target.refConfig.selector,
                            propName: prop,
                            functionArgs: args
                        },
                        (msg) => {
                            resolve(msg.content);
                        }))
                });
            };

            //TODO: we can implement things like auto serialization and binding functions here
            //getterFunction.test = '123';

            return getterFunction;

            //return Reflect.get(target, prop);
        },
        set(target, prop: string, value) {
            component.send(new CustomRefMessage(({ref, propName, propValue}) => {
                    if (ref)
                        // @ts-ignore
                        ref[propName] = propValue;
                },
                {
                    //TODO : fix that
                    elementId: target.refConfig.selector,
                    propValue: value,
                    propName: prop
                },
                (msg) => {
                    //TODO : log?
                }))
            return true;
        },
    });
}

export const createRefComponent = async <T extends IMyopComponent = IMyopComponent>(ref: Ref, componentConfig: IComponentConfig, components: IMyopComponent[], options?: loaderOptions): Promise<T> => {
    const component = ref.component;
    if (!component)
        throw new Error('cant createRefComponent with detached ref');

    //console.log('createRefComponent called', ref, componentConfig);

    return new Promise(async (resolve, reject) => {
        const refComponent = new MyopRefComponent(componentConfig.type, component, options);
        components.push(refComponent);

        //for cases where ref components define in the component config
        // we need to wait for the root component the be initiated before interacting with it
        await component.initiated();


        //TODO :
        const timeout = setTimeout(() => {
            //reject('timeout?');
            reject('timeout');
        }, 5000);

        component.send(new CreateRefComponentMessage(ref.refConfig,
            componentConfig,
            {
                ...options ? options : {},
                id: refComponent.id,
                _hasParent: true
            },
            async (message) => {

                //TODO : check if timeout already passed
                clearTimeout(timeout);

                if (message.failed) {
                    //TODO :
                    reject('CreateRefComponentMessage failed');
                    return;
                }

                const implementationComponent = components.find(c => c.id == refComponent.id);

                //same memory env, same HostSDK
                if (implementationComponent !== refComponent) {
                    //TODO : clean refComponent ?

                    component.refs[ref.refConfig.name] = implementationComponent;
                    resolve(implementationComponent as unknown as T);
                    return;
                } else {
                    refComponent.setInitiated()
                    component.refs[ref.refConfig.name] = refComponent;
                }

                //TODO : here >
                //TODO : what about reapters ?! dynamic ?!
                // const exsistingRefComponent = component.refs[ref.refConfig.name] as MyopRefComponent;
                // if (exsistingRefComponent.id && exsistingRefComponent.parent) {
                //     component.refs[ref.refConfig.name] = [component.refs[ref.refConfig.name], refComponent];
                // } else if (Array.isArray(component.refs[ref.refConfig.name])) {
                //     component.refs[ref.refConfig.name].push(refComponent)
                // } else {
                //   component.refs[ref.refConfig.name] = refComponent; //hostSDK.components.find(c => c.id === refComponent.id)!;
                // }

                //TODO : in webcomponent case the same id will alreday be here, which is good !
                // name:
                //     id
                //     componentDefinitionId

                // for inner components inside ref component :
                message.nestedRefs.forEach(nestedRefConfig => {
                    const nestedComponentDefinition = componentConfig.instance.resolvedNestedComponents!.find(c => c.type.id === nestedRefConfig.componentDefinitionId)!.type;
                    //const nestedComponentDefinition = component.componentDefinition.resolvedDefinitions?.find(def => def.id === nestedRefConfig.componentDefinitionId)!;
                    // const refConfig = componentConfig.type.refs.find(r => r.name === nestedRefConfig.refName);
                    // if (!refConfig) {
                    //     throw new Error('no refConfig found for nested ref');
                    // }

                    //debugger;

                    const nestedComponent = new MyopRefComponent(nestedComponentDefinition, refComponent);
                    nestedComponent.setInitiated();
                    refComponent.refs[nestedRefConfig.refName] = nestedComponent;
                    components.push(nestedComponent);
                });


                try {
                    await connectRefs(refComponent, componentConfig, components, true);

                    //TODO :use config check for auto-hight and convert type properly
                    // @ts-ignore
                    if (component.setHeightBasedOnScrollHeight)
                        // @ts-ignore
                        component.setHeightBasedOnScrollHeight();

                    resolve(refComponent as unknown as T);
                } catch (e) {
                    //debugger
                    reject(e);
                    // throw e
                }
            }));
    })
}

export const connectRefs = async (component: IMyopComponent, componentConfig: IComponentConfig, components: IMyopComponent[], withoutRefComponents: boolean = false) => {

    if (!component.refs)
        component.refs = {};

    const waitFor = Object.values(component.componentDefinition.refs).map(ref => {
        return new Promise<void>(async (resolve, reject) => {
            if (ref.behavior.type == RefBehaviorType.component) {
                // component.refs[ref.name] =

                if (withoutRefComponents) {
                    resolve();
                    return;
                }

                // component.
                //TODO : change to get it from the flow
                let refComponentId = (ref.behavior as IComponentRefBehavior).componentId;
                if (!refComponentId)
                    refComponentId = (ref.behavior as IComponentRefBehavior).instance.componentId

                // if (!component.componentDefinition.resolvedDefinitions) {
                //     reject(new Error('componentConfig provided without resolvedDefinitions check the config object'));
                //     //throw new Error('componentConfig provided without resolvedDefinitions check the config object');
                // }
                // const componentDefinitionConfig = component.componentDefinition.resolvedDefinitions!.find(compdef => compdef.id === refComponentId)!;
                // //const componentDefinitionConfig = await getComponentDefinitionConfig(componentInstanceConfig.componentId);
                //
                // if (!componentDefinitionConfig) {
                //     reject(new Error('componentConfig provided without resolvedDefinitions check the config object'));
                //     return;
                // }

                //TODO: if we have the same component more than once we will need instance id here ...
                const nestedComponentConfig = componentConfig.instance.resolvedNestedComponents?.find(nestedComponent => nestedComponent.type.id === refComponentId);

                if (!nestedComponentConfig) {
                    throw new Error('componentConfig provided without nestedComponentConfig check the config object');
                    return;
                }

                //const componentInstanceConfig = componentConfig.instance.nestedComponents?.find(componentInstace => componentInstace.componentId === componentDefinitionConfig.id);
                // if(!componentDefinitionConfig){
                //     throw new Error('TODO TODO TODO ');
                //     return;
                // }

                //TODO :think about that :
                //   componentDefinitionConfig.resolvedDefinitions = component.componentDefinition.resolvedDefinitions;
                //   const nestedComponentConfig: IComponentConfig = {
                //       instance: nestedComponentConfig.instance!,
                //       type: nestedComponentConfig.type,
                //       name: nestedComponentConfig.name
                //   }

                const _ref = Ref.create('', ref, component);

                try {
                    //console.log('nested !! createRefComponent called');
                    const nestedComponent = await createRefComponent(_ref, nestedComponentConfig!, components)
                    //TDOO : put that ref component somewhere?

                    // debugger;
                    component.refs[ref.name] = nestedComponent;

                    resolve();
                } catch (e) {
                    reject(e);
                }
            } else {
                try {
                    const __nonProxied = Ref.create('', ref, component);
                    component.refs[ref.name] = createRefProxy(__nonProxied, component);
                    resolve();
                } catch (e) {
                    //alert('?')
                }
            }
        })
    })
    try {
        await Promise.all(waitFor);
    } catch (e) {
        //debugger
        throw e;
    }

}

export type propTypes<T extends componentDefinitionProps> = {
    [Property in keyof T]:
    T[Property] extends { mode: 'input' } ?
        T[Property]['type'] :
        Promise<T[Property]['type']>
}

export const exec = async (getter: any) => {
    return await getter()
}

export const actAs = <T>(ref: Ref) => {
    return ref as unknown as FunctionifyRef<T>
}

export type FunctionifyRef<T = any> = {
    [K in keyof T]: T[K] extends (...args: infer A) => infer R // If it's a function
        ? (...args: A) => Promise<R> // Convert it to a promised function
        : T[K]
};
